
import React, { useState, useEffect } from 'react';
import { ref, onValue, push, set, remove, update } from 'firebase/database';
import { db } from '../../firebase';
import { Coupon } from '../../types';
import { Plus, Trash2, X, Check, TicketPercent, Calendar, ToggleLeft, ToggleRight, DollarSign, Percent, Info, Activity } from 'lucide-react';

const AdminCoupons: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage' as 'percentage' | 'fixed',
    value: 0,
    isActive: true,
    isSingleUse: false,
    expiryDate: ''
  });

  useEffect(() => {
    const couponsRef = ref(db, 'coupons');
    const unsubscribe = onValue(couponsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setCoupons((Object.values(data) as Coupon[]).sort((a: any, b: any) => b.createdAt - a.createdAt));
      } else {
        setCoupons([]);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.code || formData.value <= 0) return;

    const newRef = push(ref(db, 'coupons'));
    const couponData: Coupon = {
      id: newRef.key as string,
      code: formData.code.toUpperCase(),
      type: formData.type,
      value: Number(formData.value),
      isActive: formData.isActive,
      isSingleUse: formData.isSingleUse,
      isUsed: false,
      usageCount: 0, // Yeni kuponlarda sayaç 0 başlar
      expiryDate: formData.expiryDate ? new Date(formData.expiryDate).getTime() : Date.now() + 30 * 24 * 60 * 60 * 1000,
      createdAt: Date.now()
    };

    await set(newRef, couponData);
    setIsModalOpen(false);
    setFormData({ code: '', type: 'percentage', value: 0, isActive: true, isSingleUse: false, expiryDate: '' });
  };

  const deleteCoupon = (id: string) => {
    if (window.confirm('Bu kuponu silmek istediğinize emin misiniz?')) {
      remove(ref(db, `coupons/${id}`));
    }
  };

  const toggleActive = async (id: string, current: boolean) => {
    await update(ref(db, `coupons/${id}`), { isActive: !current });
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="text-3xl font-black text-stone-900">Kupon & İndirim Yönetimi</h1>
          <p className="text-stone-500 font-medium italic mt-1">Sitede geçerli indirim kodlarını ve kullanım istatistiklerini izleyin.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-amber-800 text-white px-8 py-4 rounded-2xl font-bold flex items-center space-x-3 shadow-xl shadow-amber-900/20 hover:bg-amber-900 transition-all"
        >
          <Plus size={20} />
          <span>Yeni Kupon Oluştur</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {coupons.map((coupon) => (
          <div key={coupon.id} className={`bg-white rounded-[2.5rem] border ${coupon.isActive ? 'border-amber-200' : 'border-stone-100'} shadow-sm overflow-hidden flex flex-col group relative`}>
             <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                   <div className="w-16 h-16 bg-stone-50 rounded-2xl flex items-center justify-center text-amber-800">
                      <TicketPercent size={32} />
                   </div>
                   <div className="flex items-center space-x-2">
                      <button onClick={() => toggleActive(coupon.id, coupon.isActive)}>
                        {coupon.isActive ? <ToggleRight className="text-amber-700" size={32} /> : <ToggleLeft className="text-stone-300" size={32} />}
                      </button>
                      <button onClick={() => deleteCoupon(coupon.id)} className="p-2 text-stone-300 hover:text-red-500 transition-colors">
                        <Trash2 size={20} />
                      </button>
                   </div>
                </div>
                
                <h3 className="text-2xl font-black text-stone-900 mb-2 tracking-tight uppercase">{coupon.code}</h3>
                <div className="flex items-center space-x-3 mb-6">
                   <span className="px-3 py-1 bg-stone-100 text-stone-600 text-[10px] font-black uppercase rounded-full">
                      {coupon.type === 'percentage' ? 'Yüzdelik' : 'Sabit Tutar'}
                   </span>
                   <div className="flex items-center text-emerald-600 font-black text-xs">
                      <Activity size={14} className="mr-1" />
                      <span>{coupon.usageCount || 0} Kullanım</span>
                   </div>
                </div>

                <div className="bg-stone-50 p-6 rounded-2xl border border-stone-100 flex items-center justify-between">
                   <div>
                      <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1">İndirim Oranı</p>
                      <p className="text-2xl font-black text-stone-900">
                         {coupon.type === 'percentage' ? `%${coupon.value}` : `₺${coupon.value}`}
                      </p>
                   </div>
                   <div className="text-right">
                      <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1">Tür</p>
                      <p className="text-xs font-bold text-stone-600">{coupon.isSingleUse ? 'Tek Kullanım' : 'Çoklu Kullanım'}</p>
                   </div>
                </div>

                <div className="mt-8 flex items-center justify-between border-t border-stone-50 pt-4">
                   <div className="flex items-center text-xs font-bold text-stone-400">
                      <Calendar size={14} className="mr-1" />
                      <span>SKT: {new Date(coupon.expiryDate).toLocaleDateString('tr-TR')}</span>
                   </div>
                   {coupon.isSingleUse && coupon.isUsed && (
                      <span className="text-[10px] font-black text-red-500 bg-red-50 px-2 py-0.5 rounded uppercase">Tükendi</span>
                   )}
                </div>
             </div>
          </div>
        ))}
        {coupons.length === 0 && (
          <div className="col-span-full py-20 bg-stone-50 rounded-[3rem] border-2 border-dashed border-stone-200 flex flex-col items-center justify-center text-stone-400 text-center">
            <TicketPercent size={48} className="mb-4 opacity-20" />
            <p className="font-medium italic">Henüz indirim kuponu oluşturulmamış.</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[100] bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
            <div className="p-8 border-b border-stone-50 flex items-center justify-between bg-stone-50/50">
              <h2 className="text-2xl font-black text-stone-900">Yeni Kupon</h2>
              <button onClick={() => setIsModalOpen(false)}><X size={28} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Kupon Kodu</label>
                  <input 
                    type="text" required
                    value={formData.code}
                    onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900 font-black"
                    placeholder="Örn: YENIYIL20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">İndirim Türü</label>
                  <select 
                    value={formData.type}
                    onChange={e => setFormData({...formData, type: e.target.value as any})}
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold"
                  >
                    <option value="percentage">Yüzde (%)</option>
                    <option value="fixed">Tutar (₺)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Değer</label>
                  <input 
                    type="number" required
                    value={formData.value}
                    onChange={e => setFormData({...formData, value: Number(e.target.value)})}
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                    placeholder="Örn: 15"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Bitiş Tarihi</label>
                  <input 
                    type="date" required
                    value={formData.expiryDate}
                    onChange={e => setFormData({...formData, expiryDate: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                  />
                </div>
                <div className="flex items-center space-x-4 pt-4 ml-2">
                   <button 
                    type="button"
                    onClick={() => setFormData({...formData, isSingleUse: !formData.isSingleUse})}
                    className="flex items-center space-x-3 group"
                   >
                      <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${formData.isSingleUse ? 'bg-stone-900 border-stone-900 text-white' : 'bg-white border-stone-200 text-transparent'}`}>
                         <Check size={14} />
                      </div>
                      <span className="text-sm font-bold text-stone-600 group-hover:text-stone-900">Tek Kullanımlık</span>
                   </button>
                </div>
              </div>

              <div className="pt-8">
                 <button 
                  type="submit"
                  className="w-full bg-amber-800 text-white py-5 rounded-2xl font-bold text-lg hover:bg-amber-900 shadow-xl transition-all active:scale-95"
                >
                  Kuponu Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;
